import type Quill from 'quill/core/quill'
import merge from 'lodash/merge'
import { DeleteAction, ImageSpec, ResizeAction } from 'quill-blot-formatter'
import { useEffect, useState } from 'react'

import { type QuillEditorProps } from '..'
import useFileArchiveUpload from '@/utils/hooks/useFileArchiveUpload'
import { useCompany } from '@/utils/hooks/useCompany'

class CustomImageSpec extends ImageSpec {
  getActions() {
    return [DeleteAction, ResizeAction]
  }
}

type Props = {
  isInitialized: boolean
  quillEditorRef: React.RefObject<Quill | null>
  setTablePickerVisible: React.Dispatch<React.SetStateAction<boolean>>
  isMobile: boolean
  placeholder: QuillEditorProps['placeholder']
}

type Return = NonNullable<QuillEditorProps['options']> | null

const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp']

const useOptions = (props: Props): Return => {
  const {
    isInitialized,
    quillEditorRef,
    setTablePickerVisible,
    isMobile,
    placeholder,
  } = props

  const { id: companyId } = useCompany()
  const [uploadImageMutation] = useFileArchiveUpload({
    onCompleted: () => {
      // Handle successful upload if needed
    },
  })

  const [options, setOptions] = useState<NonNullable<
    QuillEditorProps['options']
  > | null>(null)

  useEffect(() => {
    if (isInitialized) return // Make sure to only set options once

    const defaultOptions: NonNullable<QuillEditorProps['options']> = {
      theme: 'snow',
      placeholder,
      modules: {
        table: true,
        blotFormatter: {
          specs: [CustomImageSpec],
        },
        toolbar: {
          container: [
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            [
              { align: '' },
              { align: 'center' },
              { align: 'right' },
              { align: 'justify' },
            ],
            ['bold', 'italic', 'underline', 'strike'],
            [{ color: [] }, { background: [] }],
            ['blockquote', 'link'],

            [{ list: 'ordered' }, { list: 'bullet' }],
            ['image', 'video'],
            ['table'],
          ],
          handlers: {
            table: function (value: any) {
              if (value) {
                setTablePickerVisible(true)
              }
            },
            image: () => {
              // 1. Lag din egen input med 'accept'
              const input = document.createElement('input')
              input.setAttribute('type', 'file')
              input.setAttribute('accept', ALLOWED_IMAGE_TYPES.join(','))
              input.click()

              input.onchange = async () => {
                if (!quillEditorRef.current || !input?.files) return

                const file = input.files?.[0]
                if (!file) return

                // Validate filtype
                if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
                  alert('Unsupported image type!')
                  return
                }

                // Get quill instance
                const quill = quillEditorRef.current
                const range = quill.getSelection(true) ?? {
                  index: 0,
                  length: 0,
                }

                try {
                  // Upload image
                  const response = await uploadImageMutation({
                    variables: {
                      input: {
                        file: file,
                        description: '',
                        company: companyId,
                        status: 'PENDING',
                      },
                    },
                  })

                  // Extract image URL from response
                  const id = response.data?.createFileArchive.fileArchive.id
                  const imageUrl =
                    response.data?.createFileArchive.fileArchive.url

                  // If image is returned, insert it into the editor
                  if (imageUrl) {
                    quill.insertEmbed(range.index, 'image', imageUrl)
                    quill.formatText(range.index, 1, {
                      width: '100%',
                      height: 'auto',
                      'data-id': id,
                    })
                    quill.setSelection(range.index + 1)
                  }

                  // Otherwise, throw error
                  else throw new Error('No image URL returned from upload')
                } catch (error) {
                  console.error('Upload failed', error)
                }
              }
            },
          },
        },
      },
    }

    const mobileOptions: NonNullable<QuillEditorProps['options']> = {
      modules: {
        toolbar: {
          container: [
            { header: [1, 2, 3, 4, 5, 6, false] },
            { align: '' },
            { align: 'center' },
            { align: 'right' },
            { align: 'justify' },
            'bold',
            'italic',
            'underline',
            'strike',
            { color: [] },
            { background: [] },
            'blockquote',
            'link',
            { list: 'ordered' },
            { list: 'bullet' },
            'image',
            'video',
            'table',
          ],
        },
      },
    }

    if (isMobile) setOptions(merge({}, defaultOptions, mobileOptions))
    else setOptions(defaultOptions)
  }, [
    companyId,
    isMobile,
    isInitialized,
    placeholder,
    quillEditorRef,
    setTablePickerVisible,
    uploadImageMutation,
  ])

  return options
}

export default useOptions
